/**
 * PlansSection — standalone agent-dashboard section listing every enabled
 * product whose category is named "Plans" (case-insensitive), shown as
 * rows rather than the card grid used elsewhere (e.g. BuyProductsTab).
 * Purchases happen at the agent-account level (no property attached) via
 * POST /property-admin/products/buy-plan, unlike the per-property
 * Buy Products flow.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Layers, ShoppingBag, CheckCircle2 } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { ProductBadge } from "@/components/ProductBadge";
import BillingIntervalToggle from "@/components/BillingIntervalToggle";
import { defaultInterval, formatIntervalPrice } from "@/utils/priceIntervals";

const API = getApiUrl();

export default function PlansSection() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/property-admin/products/plans`, { withCredentials: true });
        if (alive) setProducts(data.products || []);
      } catch (e) {
        if (!alive) return;
        toast.error(e?.response?.data?.detail || "Failed to load plans");
        setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const buyPlan = async (productId, interval) => {
    setBuyingId(productId);
    try {
      const { data } = await axios.post(
        `${API}/property-admin/products/buy-plan`,
        { product_id: productId, origin_url: window.location.origin, billing_interval: interval },
        { withCredentials: true },
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setBuyingId(null);
    }
  };

  return (
    <div data-testid="plans-section-content">
      <div className="flex items-center gap-2 mb-4">
        <Layers className="h-5 w-5 text-muted-foreground" />
        <h2 className="font-serif text-xl">Plans</h2>
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : products.length === 0 ? (
        <div
          className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground"
          data-testid="plans-section-empty"
        >
          No plans available yet.
        </div>
      ) : (
        <div className="flex flex-col divide-y divide-border border border-border rounded" data-testid="plans-section-list">
          {products.map((p) => (
            <PlanRow key={p.product_id} product={p} isBuying={buyingId === p.product_id} onBuy={buyPlan} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlanRow({ product: p, isBuying, onBuy }) {
  const [billingInterval, setBillingInterval] = useState(() => defaultInterval(p));
  const src = p.image_url || "";
  const fitClass = p.image_fit === "cover" ? "object-cover" : "object-contain";
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(); } catch { return ""; }
  };
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 ${p.purchased ? "bg-emerald-50/30" : "bg-background"}`}
      data-testid={`plans-row-${p.product_id}`}
    >
      <div className="relative w-16 h-16 flex-shrink-0 bg-muted/40 flex items-center justify-center overflow-hidden rounded">
        {src ? (
          <img src={src} alt={p.name} className={`w-full h-full ${fitClass}`} />
        ) : (
          <ShoppingBag className="h-5 w-5 text-muted-foreground/40" />
        )}
        {p.badge_text && (
          <ProductBadge
            text={p.badge_text}
            position={p.badge_position}
            bgColor={p.badge_bg_color}
            textColor={p.badge_text_color}
            fontFamily={p.badge_font_family}
            fontSize={p.badge_font_size}
            rotation={p.badge_rotation}
            testId={`plans-row-badge-${p.product_id}`}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate" data-testid={`plans-row-name-${p.product_id}`}>{p.name}</div>
        {p.short_description && (
          <p className="text-xs text-muted-foreground line-clamp-1">{p.short_description}</p>
        )}
        {p.supplier_name && (
          <div className="text-[11px] text-muted-foreground">Supplied by {p.supplier_name}</div>
        )}
        {!p.purchased && (
          <div className="mt-1.5">
            <BillingIntervalToggle product={p} value={billingInterval} onChange={setBillingInterval} testIdPrefix="plans-row-interval" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 sm:flex-col sm:items-end justify-between sm:justify-center flex-shrink-0">
        {!p.purchased && (
          <div className="font-serif text-base whitespace-nowrap" data-testid={`plans-row-price-${p.product_id}`}>
            {formatIntervalPrice(p, billingInterval)}
          </div>
        )}
        {p.purchased ? (
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded whitespace-nowrap ${
              p.expired ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
            }`}
            data-testid={`plans-row-purchased-${p.product_id}`}
            title={p.purchased_at ? `Bought ${fmtDate(p.purchased_at)}` : undefined}
          >
            <CheckCircle2 className="h-3 w-3" />
            {p.expired
              ? `Expired ${fmtDate(p.expires_at)}`
              : p.expires_at
                ? `Purchased ${fmtDate(p.purchased_at)} · Expires ${fmtDate(p.expires_at)}`
                : p.purchased_at
                  ? `Purchased ${fmtDate(p.purchased_at)}`
                  : "Purchased"}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onBuy(p.product_id, billingInterval)}
            disabled={isBuying}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-primary text-primary-foreground rounded whitespace-nowrap disabled:opacity-60"
            data-testid={`plans-row-buy-${p.product_id}`}
          >
            {isBuying ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            {isBuying ? "Starting checkout…" : "Buy now"}
          </button>
        )}
      </div>
    </div>
  );
}
